import { forwardRef, type ReactNode, type Ref } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../_internal/factory.js';
import { useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import './Chip.css';

export interface ChipProps extends BaseProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  children?: ReactNode;
  /** filled | outline | light */
  variant?: 'filled' | 'outline' | 'light';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  value?: string;
  name?: string;
}

/** Toggleable chip/filter pill. */
export const Chip = forwardRef(function Chip(
  { checked, defaultChecked, onChange, children, variant = 'outline', size = 'sm', value, name, ...rest }: ChipProps,
  ref: Ref<HTMLLabelElement>
): ReactNode {
  const [val, setVal] = useUncontrolled<boolean>({
    value: checked,
    defaultValue: defaultChecked,
    finalValue: false,
    onChange
  });

  const base = useBaseAttrs('rr-chip', rest, [
    `rr-chip--${variant}`,
    `rr-chip--size-${size}`,
    val && 'rr-chip--checked'
  ]);
  const { dom } = splitBaseProps(rest);
  return (
    <label {...dom} ref={ref} {...base.attrs} className={base.className} style={base.style}>
      <input
        type="checkbox"
        className="rr-chip__input rr-sr-only"
        checked={val}
        name={name}
        value={value}
        onChange={(e) => setVal(e.currentTarget.checked)}
      />
      <span className="rr-chip__label">{children}</span>
    </label>
  );
});
